/**
 * @architecture-header 1
 * @layer app
 * @domain workspace
 * @responsibility Publish the context resolved by the first-login selection screen to every module.
 * @state-owner auth module active-context query; this context is a read-only projection
 * @allowed-imports React; shared view types
 * @forbidden role persistence; in-product role switching; authorization decisions
 * @public-api workspace routes and modules
 * @side-effects none
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';
import type { WorkspaceRole } from '../../shared/lib/model';

export interface ActiveWorkspaceContext {
  readonly role: WorkspaceRole;
  /** The access-context id every module query key is scoped by. */
  readonly contextId: string;
}

const ActiveContext = createContext<ActiveWorkspaceContext | null>(null);

export function ActiveRoleProvider({
  children,
  contextId,
  role,
}: PropsWithChildren<{ contextId: string; role: WorkspaceRole }>) {
  const value = useMemo(() => ({ contextId, role }), [contextId, role]);
  return <ActiveContext.Provider value={value}>{children}</ActiveContext.Provider>;
}

export function useActiveWorkspaceContext(): ActiveWorkspaceContext {
  const value = useContext(ActiveContext);
  if (!value) throw new Error('ActiveRoleProvider is missing');
  return value;
}

export function useActiveRole(): WorkspaceRole {
  return useActiveWorkspaceContext().role;
}

export function useActiveContextId(): string {
  return useActiveWorkspaceContext().contextId;
}

export function useIsTeacher(): boolean {
  return useActiveRole() === 'teacher';
}
